import type BaseObject from "../models/BaseObject";
import type Connection from "../models/Connection";
import type { ConnectionEndpoint } from "./ConnectionEndpointProvider";
import { DEFAULT_NAVIGATOR_ANCHOR_OPTIONS, type NavigatorAnchorOptions } from "./navigatorAnchorOptions";

type Point = ConnectionEndpoint["point"];

/** Searches IDs from the device upwards. The measurement callback decides whether a row can supply an anchor. */
export function findNavigatorAnchor(
  device: BaseObject | undefined,
  findVisiblePoint: (id: string) => Point | undefined,
  options: Pick<NavigatorAnchorOptions, "maxParentDepth"> = DEFAULT_NAVIGATOR_ANCHOR_OPTIONS,
): Point | undefined {
  return findNavigatorAnchorTarget(device, findVisiblePoint, options)?.point;
}

/** Retains the resolved object's ID so interaction reveals the same row that supplies the anchor. */
function findNavigatorAnchorTarget(
  device: BaseObject | undefined,
  findVisiblePoint: (id: string) => Point | undefined,
  options: Pick<NavigatorAnchorOptions, "maxParentDepth">,
): { point: Point; objectId: string } | undefined {
  const requestedDepth = options.maxParentDepth;
  const depth =
    requestedDepth === null || (Number.isFinite(requestedDepth) && requestedDepth >= 0)
      ? requestedDepth
      : DEFAULT_NAVIGATOR_ANCHOR_OPTIONS.maxParentDepth;
  const maxDepth = depth === null ? Infinity : Math.floor(depth);
  const visited = new Set<string>();
  let candidate = device;
  for (let level = 0; candidate && level <= maxDepth; level++) {
    if (candidate.isDeleted || candidate.className === "Root" || visited.has(candidate.id)) return;
    visited.add(candidate.id);
    const point = findVisiblePoint(candidate.id);
    if (point) return { point, objectId: candidate.id };
    candidate = candidate.parent;
  }
}

/** Supplies one navigator anchor per connection side, avoiding simultaneous ancestor anchors. */
export function navigatorConnectionEndpoints(
  connection: Connection,
  findVisiblePoint: (id: string) => Point | undefined,
  options: Pick<NavigatorAnchorOptions, "maxParentDepth"> = DEFAULT_NAVIGATOR_ANCHOR_OPTIONS,
): ConnectionEndpoint[] {
  const result: ConnectionEndpoint[] = [];
  const start = findNavigatorAnchorTarget(connection.fromDevice, findVisiblePoint, options);
  const end = findNavigatorAnchorTarget(connection.toDevice, findVisiblePoint, options);
  if (start) result.push({ connectionId: connection.id, role: "start", point: start.point, anchorObjectId: start.objectId, surface: "navigator", exitDirection: { x: 1, y: 0 } });
  if (end) result.push({ connectionId: connection.id, role: "end", point: end.point, anchorObjectId: end.objectId, surface: "navigator", exitDirection: { x: 1, y: 0 } });
  for (const endpoint of result) {
    endpoint.targetObjectId = endpoint.role === "start" ? connection.fromDevice?.id : connection.toDevice?.id;
  }
  return result;
}
